import {
  LeadSearchSummary,
  useDeleteLeadSearch,
  useLeadSearches,
  useRenameLeadSearch,
} from "@inrange/building-manager-api-client";
import { useMemo, useState } from "react";
import { Alert, Button, Container, Form, Modal, Table } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import LeadSearchPage from "./LeadSearchPage";
import SortBySelect from "./SortBySelect";
import {
  SortDirection,
  buildSortOptions,
  nextSortDirection,
} from "./sortOptions";

// Saved searches: the list of recorded searches, with the opened one rendered
// below it.
//
// The opened search is `LeadSearchPage` in "saved" mode, not a re-implementation
// of the review surface. That page owns the results table, the detail panel, the
// bulk actions and Force refresh; a second copy of any of it would drift, which
// is what every entry on the lead-pipeline "surfaces that must agree" list is.

type SortKey = "name" | "resultCount" | "promotedCount" | "lastRun";

const SORTABLE: { key: SortKey; label: string; width?: string }[] = [
  { key: "name", label: "Search", width: "44%" },
  { key: "resultCount", label: "Results" },
  { key: "promotedCount", label: "Promoted" },
  { key: "lastRun", label: "Last run" },
];

// How much of the page the list of searches may take before it scrolls inside
// itself, in rows. The opened search renders BELOW this table, so an uncapped
// list pushes the thing the operator came to look at off the screen.
const MAX_VISIBLE_SEARCHES = 4;
// One `Table size="sm"` row at fontSize 13: 13 * 1.5 line-height + 2 * 0.3rem
// of cell padding, rounded up. Only used to turn the row cap into a pixel
// height - a row or so either way just changes where the scrollbar starts.
const SEARCH_ROW_HEIGHT_PX = 33;

// Both directions of every column, so a header click always lands on a named
// option - see SortBySelect for why there is no "Column sort" fallback.
const SORT_OPTIONS = buildSortOptions<SortKey>([
  {
    key: "lastRun",
    descLabel: "Last run (newest)",
    ascLabel: "Last run (oldest)",
  },
  {
    key: "resultCount",
    descLabel: "Results (most)",
    ascLabel: "Results (fewest)",
  },
  {
    key: "promotedCount",
    descLabel: "Promoted (most)",
    ascLabel: "Promoted (fewest)",
  },
  { key: "name", descLabel: "Search (Z-A)", ascLabel: "Search (A-Z)" },
]);

const LeadSearchHistoryPage: React.FC = () => {
  document.title = "Saved searches";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("openSearch");

  const { data, isLoading, error } = useLeadSearches();
  const renameSearch = useRenameLeadSearch();

  const deleteSearch = useDeleteLeadSearch();
  // The search the confirm dialog is asking about. Held as the whole summary
  // rather than an id so the dialog can name it and quote its tagged-lead count
  // without re-finding the row (which a concurrent refetch could have dropped).
  const [deleting, setDeleting] = useState<LeadSearchSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);

  // Newest run first, matching the order the BE already returns
  // (`list_lead_searches` orders on `lastRunAt DESC`), so the default view is
  // unchanged and only an explicit click re-sorts.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDirection }>({
    key: "lastRun",
    dir: "desc",
  });

  // Memoised because the `?? []` fallback would otherwise mint a new array on
  // every render and re-run the sort below each time.
  const searches = useMemo(() => data?.searches ?? [], [data]);
  const opened = searches.find((s) => s.id === openId) ?? null;

  const sortedSearches = useMemo(() => {
    const valueFor = (s: LeadSearchSummary) => {
      switch (sort.key) {
        case "resultCount":
          return s.resultCount;
        case "promotedCount":
          return s.promotedCount;
        case "lastRun":
          return new Date(s.lastRunAt).getTime();
        case "name":
        default:
          return s.name.toLowerCase();
      }
    };
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...searches].sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      // Counts tie constantly (two searches that both promoted 0), so without a
      // final rule the order inside a tie is whatever the BE returned and moves
      // between reads. Name is the one total, stable key here - the same
      // reasoning as the results table's address tie-break. Always ascending:
      // a stable ordering, not a second thing the operator ranked on.
      if (sort.key === "name") return 0;
      return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
    });
  }, [searches, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) => ({ key, dir: nextSortDirection(key, prev) }));

  const confirmDelete = async (removeTags: boolean) => {
    if (deleting === null) return;
    setDeleteError(null);
    try {
      await deleteSearch.mutateAsync({ searchId: deleting.id, removeTags });
      // Clear the opened-search param when it was the one deleted, or the page
      // renders a search below the list that no longer exists.
      if (openId === deleting.id) setSearchParams({});
      setDeleting(null);
    } catch {
      setDeleteError(`Could not delete "${deleting.name}".`);
    }
  };

  const startRename = (search: LeadSearchSummary) => {
    setRenamingId(search.id);
    setRenameValue(search.name);
    setRenameError(null);
  };

  const submitRename = async (search: LeadSearchSummary) => {
    const name = renameValue.trim();
    if (!name || name === search.name) {
      setRenamingId(null);
      setRenameError(null);
      return;
    }
    setRenameError(null);
    try {
      await renameSearch.mutateAsync({ searchId: search.id, name });
      setRenamingId(null);
    } catch (err) {
      // A 409 means another search already holds this name. Surfaced inline
      // rather than swallowed: the rename did not happen, and merging the two
      // would leave their leads sharing one tag with no way to separate them.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      const detail = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setRenameError(
        status === 409
          ? (detail ??
              `A search called "${name}" already exists. Pick a different name.`)
          : "Could not rename this search."
      );
    }
  };

  return (
    <Container fluid style={{ padding: 0, flex: 1, minHeight: 0 }}>
      <div
        style={{
          padding: "8px 12px",
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          rowGap: 6,
        }}
      >
        <h5 style={{ margin: 0 }}>Saved searches</h5>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => navigate("/leads/search")}
        >
          New search
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => navigate("/leads/import")}
        >
          Bulk load leads
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => navigate("/leads")}
        >
          View all leads
        </Button>
      </div>

      {error != null && (
        <Alert variant="danger" style={{ margin: 8 }}>
          Could not load your saved searches.
        </Alert>
      )}
      {renameError && (
        <Alert variant="danger" style={{ margin: 8 }}>
          {renameError}
        </Alert>
      )}
      {deleteError && (
        <Alert variant="danger" style={{ margin: 8 }}>
          {deleteError}
        </Alert>
      )}

      {/* This board has no filter chips, but the row is the same one the other
          two boards use, so the control sits in the same place at the same size
          on all three. */}
      <div
        style={{
          padding: "6px 12px",
          borderBottom: "1px solid #eee",
          fontSize: 13,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 4,
        }}
      >
        <SortBySelect
          options={SORT_OPTIONS}
          sortKey={sort.key}
          sortDir={sort.dir}
          onChange={(key, dir) => setSort({ key, dir })}
          ariaLabel="Sort saved searches by"
        />
      </div>

      <div style={{ padding: "0 12px" }}>
        <div
          style={{
            // Four rows plus the header, then scroll inside. Every distinct
            // search name is a permanent row (the BE caps the list at 200), so
            // uncapped this table grows until it pushes the opened search off
            // the bottom of the screen.
            maxHeight: SEARCH_ROW_HEIGHT_PX * (MAX_VISIBLE_SEARCHES + 1),
            overflowY: "auto",
          }}
        >
          <Table size="sm" hover style={{ marginBottom: 0, fontSize: 13 }}>
            <thead>
              <tr>
                {/* One trailing unsortable column for the delete action - it
                    is per-row, not something to rank on. */}
                {SORTABLE.map(({ key, label, width }) => (
                  <th
                    key={key}
                    style={{
                      width,
                      // The header would otherwise scroll away with the rows,
                      // leaving the sort arrows unreachable mid-list.
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 1,
                    }}
                  >
                    <Button
                      variant="link"
                      onClick={() => toggleSort(key)}
                      style={{
                        padding: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        color: "inherit",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                      title={`Sort by ${label.toLowerCase()}`}
                    >
                      {label}
                      {/* Every column carries an arrow, dimmed until it is the one
                        being sorted on. Showing it only on the active column
                        left the other three looking like plain header text, so
                        the control read as decoration on one header rather than
                        an affordance on all four. */}
                      <span
                        style={{
                          marginLeft: 4,
                          color: sort.key === key ? "#495057" : "#ced4da",
                        }}
                      >
                        {sort.key === key && sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    </Button>
                  </th>
                ))}
                <th
                  style={{
                    width: 32,
                    position: "sticky",
                    top: 0,
                    background: "#fff",
                    zIndex: 1,
                  }}
                />
              </tr>
            </thead>
            <tbody>
              {sortedSearches.map((search) => (
                <tr
                  key={search.id}
                  style={{
                    cursor: "pointer",
                    background: search.id === openId ? "#e7f1ff" : undefined,
                  }}
                  onClick={() => setSearchParams({ openSearch: search.id })}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    {renamingId === search.id ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Form.Control
                          size="sm"
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void submitRename(search);
                            if (e.key === "Escape") {
                              setRenamingId(null);
                              setRenameError(null);
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          disabled={renameSearch.isLoading}
                          onClick={() => void submitRename(search)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setRenamingId(null);
                            setRenameError(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                        }}
                        onClick={() =>
                          setSearchParams({ openSearch: search.id })
                        }
                      >
                        <span
                          style={{
                            fontWeight: search.id === openId ? 500 : 400,
                          }}
                        >
                          {search.name}
                        </span>
                        <Button
                          size="sm"
                          variant="link"
                          style={{ padding: 0, lineHeight: 1 }}
                          title={
                            search.promotedCount > 0
                              ? `Rename. Also retags ${search.promotedCount} promoted lead${
                                  search.promotedCount === 1 ? "" : "s"
                                }.`
                              : "Rename this search"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            startRename(search);
                          }}
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          >
                            <path
                              d="M11.5 2.5l2 2L6 12l-2.6.6L4 10z"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                      </div>
                    )}
                  </td>
                  <td>{search.resultCount}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {search.promotedCount > 0 ? (
                      <Button
                        size="sm"
                        variant="link"
                        style={{ padding: 0 }}
                        title="Show these leads in the leads list"
                        // `savedSearch`, not `tags`: the count means "rows of this
                        // search that are leads", which includes rows promoted
                        // from a different search and so carrying a different tag,
                        // so a tag filter would show fewer than the number clicked.
                        onClick={() =>
                          navigate(`/leads?savedSearch=${search.id}`)
                        }
                      >
                        {search.promotedCount}
                      </Button>
                    ) : (
                      search.promotedCount
                    )}
                  </td>
                  <td>{new Date(search.lastRunAt).toLocaleString("en-GB")}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* The existing-site buckets are standing searches defined
                        in the backend, which re-seeds them on every list read
                        and refuses the delete - so offering the control would
                        only ever strip the tag off their promoted leads and
                        then put the row straight back. */}
                    {!search.isFixture && (
                      <Button
                        size="sm"
                        variant="link"
                        style={{ padding: 0, lineHeight: 1, color: "#b02a37" }}
                        title="Delete this search"
                        onClick={() => {
                          setDeleteError(null);
                          setDeleting(search);
                        }}
                        aria-label={`Delete ${search.name}`}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path
                            d="M2.5 4h11M6.5 4V2.5h3V4M4 4l.7 9.5h6.6L12 4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!isLoading && searches.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: "#6c757d" }}>
                    No saved searches yet. Every search you run is kept here.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={deleting !== null} onHide={() => setDeleting(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            Delete saved search
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 14 }}>
          <p style={{ marginBottom: 0 }}>
            You&apos;re about to delete a saved search. Do you want to remove
            the tag <strong>{deleting?.name}</strong> from all the leads that
            carry it?
          </p>
        </Modal.Body>
        {/* `size="sm"` plus no-wrap: the three labels are long enough that at
            the default button size Cancel wrapped onto its own row, which read
            as a separate step rather than the third option. */}
        <Modal.Footer style={{ flexWrap: "nowrap" }}>
          <Button
            size="sm"
            variant="danger"
            onClick={() => void confirmDelete(true)}
            disabled={deleteSearch.isLoading}
            style={{ whiteSpace: "nowrap" }}
          >
            Delete search &amp; tags
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => void confirmDelete(false)}
            disabled={deleteSearch.isLoading}
            style={{ whiteSpace: "nowrap" }}
          >
            Delete search, keep tags
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setDeleting(null)}
            disabled={deleteSearch.isLoading}
            style={{ whiteSpace: "nowrap" }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {opened && (
        <div
          style={{
            marginTop: 12,
            borderTop: "3px solid #e7f1ff",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <LeadSearchPage
            // Remount on a different search so its internal candidate state and
            // one-shot open effect start clean rather than showing the previous
            // search's rows.
            key={opened.id}
            mode="saved"
            savedSearchId={opened.id}
            savedSearchName={opened.name}
          />
        </div>
      )}
    </Container>
  );
};

export default LeadSearchHistoryPage;
